import { useEffect, useState } from "react";
import { api, API, formatCurrency, formatApiErrorDetail } from "@/magazzino/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Warning, Upload, DownloadSimple, Barcode, Printer, ImageSquare, Camera, X } from "@phosphor-icons/react";
import { printLabels, LABEL_FORMATS } from "@/magazzino/lib/labels";

const empty = {
  sku: "", barcode: "", name: "", description: "",
  category_id: "", supplier_id: "",
  unit: "pz", quantity: 0, min_stock: 0, price: 0
};

export default function ProductsPage() {
  const isAlluminio = window.location.pathname.startsWith("/gestionale/magazzino-alluminio");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [labelSel, setLabelSel] = useState({}); // { productId: copies }
  const [labelFormat, setLabelFormat] = useState("70x37");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [removePhoto, setRemovePhoto] = useState(false);
  const [photoView, setPhotoView] = useState(null);
  const [saving, setSaving] = useState(false);

  const productPhotoUrl = (product) => product?.photo_path
    ? `${API}/gestionale/magazzino/prodotti/${product.id}/foto?magazzino_tipo=${isAlluminio ? "alluminio" : "standard"}&v=${encodeURIComponent(product.photo_updated_at || "1")}`
    : "";

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (catFilter !== "all") params.category_id = catFilter;
    if (supplierFilter) params.supplier_id = supplierFilter;
    const [p, c, s] = await Promise.all([
      api.get("/prodotti", { params }),
      api.get("/categorie"),
      api.get("/fornitori"),
    ]);
    setProducts(p.data); setCategories(c.data); setSuppliers(s.data);
  };
  useEffect(() => { load(); }, [q, catFilter, supplierFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetPhoto = () => {
    if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null); setPhotoPreview(""); setRemovePhoto(false);
  };
  const openNew = () => { setEditing(null); setForm(empty); resetPhoto(); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p, category_id: p.category_id || "", supplier_id: p.supplier_id || "" });
    setPhotoFile(null); setPhotoPreview(productPhotoUrl(p)); setRemovePhoto(false);
    setOpen(true);
  };

  const choosePhoto = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Seleziona una fotografia");
    if (file.size > 15 * 1024 * 1024) return toast.error("La fotografia supera 15 MB");
    if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setRemovePhoto(false);
  };

  const clearPhoto = () => {
    if (photoPreview?.startsWith("blob:")) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null); setPhotoPreview(""); setRemovePhoto(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity), min_stock: Number(form.min_stock), price: Number(form.price),
        category_id: form.category_id || null, supplier_id: form.supplier_id || null,
      };
      const response = editing
        ? await api.put(`/prodotti/${editing.id}`, payload)
        : await api.post("/prodotti", payload);
      const productId = response.data.id;
      let photoError = false;
      try {
        if (photoFile) {
          const body = new FormData(); body.append("file", photoFile);
          await api.post(`/prodotti/${productId}/foto`, body, { headers: { "Content-Type": "multipart/form-data" } });
        } else if (editing && removePhoto && editing.photo_path) {
          await api.delete(`/prodotti/${productId}/foto`);
        }
      } catch {
        photoError = true;
      }
      toast.success(editing ? "Prodotto aggiornato" : "Prodotto creato");
      if (photoError) toast.warning("Prodotto salvato, ma non è stato possibile aggiornare la fotografia");
      setOpen(false); resetPhoto(); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"? Verranno rimossi anche i movimenti associati.`)) return;
    await api.delete(`/prodotti/${p.id}`);
    toast.success("Prodotto eliminato");
    load();
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  const downloadTemplate = () => {
    const csv = "sku;nome;descrizione;categoria;fornitore;um;quantità;scorta_minima;prezzo\n" +
                "ART001;Vite M6x20;Vite testa esagonale;Ferramenta;Bulloneria SRL;pz;500;100;0.05\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "template_prodotti.csv"; a.click();
  };

  const doImport = async () => {
    if (!importFile) return toast.error("Seleziona un file CSV o Excel");
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/prodotti/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Import completato: ${data.created} creati, ${data.updated} aggiornati`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const toggleLabel = (p) => {
    setLabelSel((s) => {
      const next = { ...s };
      if (next[p.id]) delete next[p.id];
      else next[p.id] = 1;
      return next;
    });
  };

  const doPrintLabels = () => {
    const items = Object.entries(labelSel)
      .map(([id, copies]) => ({ product: products.find(p => p.id === id), copies }))
      .filter(x => x.product);
    if (items.length === 0) return toast.error("Seleziona almeno un prodotto");
    printLabels(items, { format: labelFormat });
  };

  const quickLabel = (p) => printLabels([{ product: p, copies: 1 }], { format: labelFormat });

  return (
    <div className="min-w-0 space-y-6 pb-[max(0.5rem,env(safe-area-inset-bottom))] lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:gap-6 lg:space-y-0 lg:pb-0">
      {/* Toolbar */}
      <Card className="border-slate-200 rounded-md shadow-none p-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex flex-1 gap-3 flex-col sm:flex-row">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input
              placeholder="Cerca per nome, SKU o Barcode/EAN…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-10"
              data-testid="products-search-input"
            />
          </div>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger className="h-10 w-full bg-white sm:w-[220px]" data-testid="products-category-filter">
              <SelectValue placeholder="Tutte le categorie" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <SearchableCombobox
            value={supplierFilter}
            onValueChange={setSupplierFilter}
            options={[
              { value: "", label: "Tutti i fornitori" },
              ...suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })),
            ]}
            placeholder="Tutti i fornitori"
            searchPlaceholder="Cerca fornitore..."
            emptyText="Nessun fornitore trovato"
            className="w-full sm:w-[220px]"
            testId="products-supplier-filter"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="h-10 border-slate-200">
            <DownloadSimple weight="bold" size={16} className="mr-1.5"/> Template
          </Button>
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) { setImportFile(null); setImportResult(null); } }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-products-btn" className="h-10 border-slate-200">
                <Upload weight="bold" size={16} className="mr-1.5"/> Importa
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-xl" data-testid="import-dialog">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Importa prodotti da CSV / Excel</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="text-sm text-slate-600">
                  Colonne accettate: <span className="tabular font-medium">sku, nome, descrizione, categoria, fornitore, um, quantità, scorta_minima, prezzo</span>.
                  Categorie e fornitori mancanti vengono creati automaticamente. Gli SKU esistenti vengono aggiornati.
                </div>
                <Input type="file" accept=".csv,.xlsx,.xls"
                       onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                       data-testid="import-file-input" className="cursor-pointer"/>
                {importResult && (
                  <div className="text-sm border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="import-result">
                    <div><b>{importResult.created}</b> creati · <b>{importResult.updated}</b> aggiornati · <b>{importResult.total}</b> righe lette.</div>
                    {importResult.errors?.length > 0 && (
                      <details className="mt-2">
                        <summary className="text-red-600 cursor-pointer">{importResult.errors.length} errori</summary>
                        <ul className="mt-2 space-y-1 text-xs text-red-600 list-disc list-inside">
                          {importResult.errors.slice(0, 20).map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportOpen(false)}>Chiudi</Button>
                <Button onClick={doImport} disabled={!importFile || importing}
                        className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="import-confirm-btn">
                  {importing ? "Importazione…" : "Importa"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={labelsOpen} onOpenChange={setLabelsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="labels-btn" className="h-10 border-slate-200">
                <Barcode weight="bold" size={16} className="mr-1.5"/> Etichette
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl" data-testid="labels-dialog">
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">Genera etichette con barcode</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600">Formato foglio</Label>
                  <Select value={labelFormat} onValueChange={setLabelFormat}>
                    <SelectTrigger className="bg-white mt-1" data-testid="label-format-select"><SelectValue/></SelectTrigger>
                    <SelectContent className="bg-white">
                      {Object.entries(LABEL_FORMATS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-slate-500">
                  Seleziona i prodotti e imposta quante copie stampare di ciascuno.
                </div>
                <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-md" data-testid="label-product-list">
                  {products.length === 0
                    ? <div className="p-6 text-center text-sm text-slate-500">Nessun prodotto disponibile.</div>
                    : products.map((p) => {
                      const selected = !!labelSel[p.id];
                      return (
                        <div key={p.id}
                          className={`flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-100 last:border-0 ${selected ? "bg-slate-50" : ""}`}>
                          <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                            <input type="checkbox" checked={selected} onChange={() => toggleLabel(p)}
                              data-testid={`label-check-${p.sku}`} className="w-4 h-4 accent-slate-900"/>
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{p.name}</div>
                              <div className="text-xs text-slate-500 tabular">
                                SKU {p.sku}{p.barcode ? ` · EAN ${p.barcode}` : ""}
                              </div>
                            </div>
                          </label>
                          {selected && (
                            <Input type="number" min="1" value={labelSel[p.id]}
                              onChange={(e) => setLabelSel((s) => ({ ...s, [p.id]: Math.max(1, parseInt(e.target.value || "1", 10)) }))}
                              className="w-20 h-8 text-right tabular"
                              data-testid={`label-copies-${p.sku}`}/>
                          )}
                        </div>
                      );
                    })}
                </div>
                <div className="text-xs text-slate-500 tabular">
                  Totale etichette: <b>{Object.values(labelSel).reduce((s, v) => s + Number(v || 0), 0)}</b>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setLabelsOpen(false)}>Annulla</Button>
                <Button onClick={doPrintLabels} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="print-labels-btn">
                  <Printer weight="bold" size={16} className="mr-1.5"/> Stampa etichette
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="new-product-btn" className="h-10 bg-slate-900 hover:bg-slate-800 text-white">
              <Plus weight="bold" size={16} className="mr-1.5"/> Nuovo prodotto
            </Button>
          </DialogTrigger>          <DialogContent className="bg-white max-w-2xl" data-testid="product-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? "Modifica prodotto" : "Nuovo prodotto"}
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="SKU *"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku-input" /></Field>
              <Field label="Barcode / EAN"><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="es. 8001234567890" data-testid="product-barcode-input" /></Field>
              <div className="sm:col-span-2"><Field label="Nome *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></Field></div>
              <Field label="Categoria">
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-white" data-testid="product-category-select"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">— Nessuna —</SelectItem>
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Fornitore">
                <Select value={form.supplier_id || "none"} onValueChange={(v) => setForm({ ...form, supplier_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-white" data-testid="product-supplier-select"><SelectValue placeholder="Seleziona…"/></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="none">— Nessuno —</SelectItem>
                    {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Unità di misura">
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="bg-white" data-testid="product-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["pz","kg","g","l","ml","m","cm","conf","box","pallet"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Prezzo unitario (€)">
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" />
              </Field>
              <Field label="Quantità iniziale">
                <Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="product-quantity-input" disabled={!!editing}/>
                {editing && <div className="text-[11px] text-slate-500 mt-1">Usa Carico/Scarico per variare la quantità.</div>}
              </Field>
              <Field label="Scorta minima">
                <Input type="number" step="0.01" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} data-testid="product-minstock-input"/>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Descrizione"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input"/></Field>
              </div>
              <div className="sm:col-span-2 border-t border-slate-200 pt-4">
                <Field label="Foto prodotto (facoltativa)">
                  <div className="mt-2 grid gap-3 sm:grid-cols-[1fr_220px]">
                    <div className="flex min-h-36 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-4 text-center">
                      <ImageSquare size={28} className="text-slate-400" />
                      <div className="flex flex-wrap justify-center gap-2">
                        <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium hover:bg-slate-100">
                          <Upload size={16} className="mr-1.5"/>Carica foto
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => choosePhoto(e.target.files?.[0])}/>
                        </label>
                        <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium hover:bg-slate-100">
                          <Camera size={16} className="mr-1.5"/>Scatta foto
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => choosePhoto(e.target.files?.[0])}/>
                        </label>
                      </div>
                      <div className="text-[11px] text-slate-500">La foto sarà compressa automaticamente</div>
                    </div>
                    <div className="relative flex min-h-36 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white">
                      {photoPreview ? <>
                        <img src={photoPreview} alt="Anteprima prodotto" className="h-36 w-full object-contain" />
                        <button type="button" onClick={clearPhoto} className="absolute right-2 top-2 rounded-full bg-white p-1.5 text-red-600 shadow" title="Rimuovi foto"><X size={16}/></button>
                      </> : <span className="text-xs text-slate-400">Nessuna foto</span>}
                    </div>
                  </div>
                </Field>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="product-cancel-btn">Annulla</Button>
              <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="product-save-btn">
                {saving ? "Salvataggio…" : editing ? "Salva modifiche" : "Crea prodotto"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 rounded-md shadow-none overflow-hidden lg:flex-1 lg:min-h-0" data-testid="products-table">
        <div className="overflow-x-auto lg:h-full lg:overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
                <th className="px-5 py-3 font-medium">SKU</th>
                <th className="px-5 py-3 font-medium">Prodotto</th>
                <th className="px-5 py-3 font-medium">Categoria</th>
                <th className="px-5 py-3 font-medium">Fornitore</th>
                <th className="px-5 py-3 font-medium text-right">Qtà</th>
                <th className="px-5 py-3 font-medium text-right">Min</th>
                <th className="px-5 py-3 font-medium text-right">Prezzo</th>
                <th className="px-5 py-3 font-medium text-right">Valore</th>
                <th className="px-5 py-3 font-medium text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 && (
                <tr><td colSpan={9} className="px-5 py-10 text-center text-slate-500">Nessun prodotto. Crea il primo articolo.</td></tr>
              )}
              {products.map((p) => {
                const low = p.quantity <= p.min_stock;
                return (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`product-row-${p.sku}`}>
                    <td className="px-5 py-3 tabular text-slate-600">{p.sku}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {p.photo_path ? <button type="button" onClick={() => setPhotoView(p)} className="h-11 w-11 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-white" title="Ingrandisci foto">
                          <img src={productPhotoUrl(p)} alt={p.name} className="h-full w-full object-cover"/>
                        </button> : <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-slate-50"><ImageSquare size={18} className="text-slate-300"/></div>}
                        <div className="min-w-0"><div className="font-medium">{p.name}</div>
                      {p.description && <div className="text-xs text-slate-500 truncate max-w-[280px]">{p.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{catName(p.category_id)}</td>
                    <td className="px-5 py-3 text-slate-600">{supName(p.supplier_id)}</td>
                    <td className={`px-5 py-3 text-right tabular font-medium ${low ? "text-red-600" : ""}`}>
                      {low && <Warning weight="bold" size={12} className="inline mr-1"/>}
                      {p.quantity} {p.unit}
                    </td>
                    <td className="px-5 py-3 text-right tabular text-slate-500">{p.min_stock}</td>
                    <td className="px-5 py-3 text-right tabular">{formatCurrency(p.price)}</td>
                    <td className="px-5 py-3 text-right tabular font-medium">{formatCurrency(p.quantity * p.price)}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => quickLabel(p)} data-testid={`label-product-${p.sku}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600" title="Stampa etichetta">
                        <Barcode size={16}/>
                      </button>
                      <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.sku}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100 text-slate-600" title="Modifica">
                        <PencilSimple size={16}/>
                      </button>
                      <button onClick={() => remove(p)} data-testid={`delete-product-${p.sku}`}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-red-50 text-red-600" title="Elimina">
                        <Trash size={16}/>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
      <Dialog open={!!photoView} onOpenChange={(value) => { if (!value) setPhotoView(null); }}>
        <DialogContent className="bg-white max-w-3xl">
          <DialogHeader><DialogTitle>{photoView?.name || "Foto prodotto"}</DialogTitle></DialogHeader>
          {photoView && <img src={productPhotoUrl(photoView)} alt={photoView.name} className="max-h-[70vh] w-full object-contain"/>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
